import moment from 'moment';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { UtilityFunctions } from '../../utils/UtilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from "redux";
import { actionCreators } from "../../store";
import { cookieService, userService } from '../../_services';
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';
import ValidationErrorToast from '../../components/validationErrorToast';
import SuccessToast from '../../components/successToast';
import SubscriptionModalAccounts from '../Dashboard/Home/SubscriptionModalAccounts';
import { api } from "../../middleware/api";
import { checkData } from "../../utils/checkData";

const styles = {
    // --- Main Layout & General ---
    container: {
        backgroundColor: '#1a1a2e', 
        minHeight: '100vh',
    },
    topBar: { 
        background: 'rgba(255, 255, 255, 0.05)',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
    },
    topBarTitle: { 
        color: '#ffffff',
        margin: 0,
        fontSize: '24px',
        fontWeight: '500',
    },
    buttonGroup: { 
        display: 'flex',
        gap: '12px',
    },
    emptyState: { 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px',
        textAlign: 'center',
        color: '#ffffff',
    },
    // --- Dashboard Specific Layout ---
    dashboardContent: { 
        display: 'flex',
        flexDirection: 'row', 
        gap: '20px', 
        marginTop: '20px', 
    },
    leftColumn: { 
        flex: '3', 
        display: 'flex',
        flexDirection: 'column',
    },
    rightColumn: { 
        flex: '2', 
        display: 'flex',
        flexDirection: 'column',
    },
    // --- Content Blocks (BlueBox) ---
    blueBox: {
        background: 'rgba(30, 30, 50, 0.75)', 
        borderRadius: '12px', 
        padding: '20px', 
        boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
        width: '100%', 
    },
    // --- Text Styles ---
    whiteText: {
        color: '#f0f0f0', 
    },
    mutedText: {
        color: 'rgba(224, 224, 224, 0.65)', 
    },
    // --- Stats Cards ---
    statsCard: {
        display: 'flex',
        alignItems: 'center',
    },
    statsContent: {
        marginLeft: '0px', 
    },
    // --- Table Styles (Invoices) ---
    table: {
        width: '100%',
        color: '#e0e0e0',
        borderCollapse: 'separate',
        borderSpacing: '0 6px', 
    },
    tableHeader: {
        color: 'rgba(224, 224, 224, 0.7)',
        fontWeight: '500',
        padding: '12px 10px', 
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)', 
        textAlign: 'left',
        fontSize: '13px', 
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    tableCell: {
        padding: '12px 10px', 
        color: '#e0e0e0',
        fontSize: '13px',
        verticalAlign: 'middle',
        lineHeight: '1.4',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)', 
    },
    tableRow: { 
        backgroundColor: 'rgba(40, 42, 60, 0.5)', 
        borderRadius: '6px', 
    },
    badge: { 
        padding: '5px 10px', 
        borderRadius: '12px', 
        fontWeight: 'bold', 
        display: 'inline-block',
        fontSize: '11px', 
        textAlign: 'center',
        minWidth: '65px', 
        lineHeight: '1.2',
    },
    // --- NEW: Missing Info Tag Style ---
    missingInfoTag: {
        display: 'inline-block',
        marginLeft: '8px',
        padding: '3px 8px', // Slightly smaller padding
        fontSize: '10px',
        fontWeight: 'bold',
        color: '#664d03', // Dark yellow text
        backgroundColor: '#fff3cd', // Light yellow background (Bootstrap's warning background)
        border: '1px solid #ffda6a', // Bootstrap's warning border
        borderRadius: '4px',
        verticalAlign: 'middle', 
        lineHeight: '1', // Ensure it doesn't increase row height much
    },
    // --- Timeline/Activity Styles ---
    timeline: {
        position: 'relative',
    },
    timelineItem: {
        position: 'relative',
        padding: '10px 0 10px 25px', 
        borderLeft: '2px solid rgba(255, 255, 255, 0.1)',
    },
    timelineDot: {
        position: 'absolute',
        left: '-5px', 
        top: '14px', 
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        zIndex: 1, 
    },
    // --- Buttons ---
    button: { 
        padding: '8px 15px', 
        borderRadius: '6px',
        border: 'none',
        cursor: 'pointer',
        display: 'inline-flex', 
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        fontSize: '14px',
        fontWeight: '500',
        transition: 'background-color 0.2s ease, opacity 0.2s ease, transform 0.1s ease',
        lineHeight: '1.2', 
        '&:hover': {
            opacity: 0.9,
        },
        '&:active': {
            transform: 'scale(0.98)',
        }
    },
    primaryButton: { 
        backgroundColor: '#007bff', 
        color: '#ffffff',
    },
    secondaryButton: { 
        backgroundColor: 'rgba(255, 255, 255, 0.15)', 
        color: '#ffffff',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.25)',
        }
    },
    actionButton: { 
        padding: '6px 10px',
        fontSize: '12px',
        borderRadius: '4px',
    },
    paginationButton: {
        padding: '6px 12px',
        fontSize: '13px',
        minWidth: '70px', 
    },
    // --- Collections Disabled Banner ---
    collectionsDisabledBanner: {
        background: 'rgba(220, 53, 69, 0.1)', 
        border: '1px solid rgba(220, 53, 69, 0.5)', 
        borderRadius: '8px',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#f8d7da', 
    },
    bannerText: {
        margin: 0,
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    enableButton: { 
        backgroundColor: '#dc3545', 
        color: '#ffffff',
        border: 'none',
        borderRadius: '4px',
        padding: '8px 16px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
    },
    // --- Search Input ---
    searchInput: {
        width: '100%',
        padding: '12px 18px',
        fontSize: '16px',
        color: '#ffffff',
        backgroundColor: 'rgba(255, 255, 255, 0.08)', 
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '8px',
        outline: 'none',
        '&:focus': {
            borderColor: 'rgba(0, 123, 255, 0.5)',
            boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
        }
    },
    // --- Tiles (from original, if still used for EmptyState) ---
    tilesContainer: {
        display: 'flex',
        gap: '24px',
        padding: '48px',
        justifyContent: 'center',
        alignItems: 'stretch',
        maxWidth: '1200px',
        margin: '0 auto',
        flexWrap: 'wrap',
    },
    tile: {
        flex: '1 1 300px',
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        padding: '48px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        minWidth: '300px',
    },
    tileIcon: {
        width: '120px',
        height: '120px',
        marginBottom: '24px',
        opacity: '0.8',
    },
    tileTitle: {
        fontSize: '24px',
        fontWeight: '500',
        marginBottom: '16px',
        color: '#ffffff',
    },
    tileText: {
        color: 'rgba(255, 255, 255, 0.6)',
        marginBottom: '24px',
        maxWidth: '400px',
        lineHeight: '1.6',
    },
    connectButton: {
        padding: '12px 24px',
        borderRadius: '6px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '500',
        backgroundColor: '#007bff',
        color: '#ffffff',
        transition: 'background-color 0.2s',
        width: '200px',
    },
};
// Helper functions
const capitalizeFirst = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const formatDuration = (seconds) => {
    if (typeof seconds !== 'number' || isNaN(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const getOutcomeColor = (outcome) => {
    switch (outcome?.toLowerCase() || '') {
        case 'positive':
            return '#28a745'; // Green
        case 'neutral':
            return '#ffc107'; // Yellow
        case 'negative':
            return '#dc3545'; // Red
        default:
            return '#6c757d'; // Grey
    }
};

// Initial state objects
const initialInvoiceDataState = {
    invoices: [],
    summary: {
        total_invoices: 0,
        overdue_summary: {
            overdue_7_days: 0,
            overdue_14_days: 0,
            overdue_30_days: 0,
            overdue_60_days: 0,
            overdue_90_days: 0
        },
        interaction_summary: {
            total_interactions: 0,
            invoices_with_payment_plans: 0,
            invoices_needing_followup: 0
        },
        stats: {
            callsToday: 0,
            totalOutstanding: 0,
            successRate: 0,
            totalCollected: 0,
            totalInvoicesCollected: 0,
        }
    }
};

const initialStatsState = {
    callsToday: 0,
    totalOutstanding: 0,
    successfulCollections: 0,
    totalCollected: 0,
    totalInvoicesCollected: 0,
};

// Empty State Component (shown when no connections)
const EmptyState = ({
    loading,
    handleXeroConnection,
    quickbooksLoading,
    handleQuickbooksConnection,
    quickbooksConnected,
    myobLoading,
    handleMyobConnection,
    zohoLoading,
    handleZohoConnection,
    handleDynamicsConnection,
    zohoBooksLoading,
    handleZohoBooksConnection,
    dynamicsLoading,
}) => (
    <div style={styles.tilesContainer}>
        {/* Xero Tile */}
        <div style={styles.tile}>
            <img 
                src={UtilityFunctions.getImageURL() + "xero.png"} 
                alt="Connect Xero"
                style={styles.tileIcon}
            />
            <h2 style={styles.tileTitle}>Connect Xero</h2>
            <p style={styles.tileText}>
                Connect your Xero account to start managing your accounts receivable. 
                Track invoices, manage collections, and automate follow-ups.
            </p>
            <button 
                style={styles.connectButton}
                disabled={loading}
                onClick={handleXeroConnection}
            >
                {loading ? "Connecting..." : "Connect Xero"}
            </button>
        </div>

        {/* QuickBooks Tile */}
        <div style={styles.tile}>
            <img 
                src={UtilityFunctions.getImageURL() + "quickbooks.webp"} 
                alt="Connect QuickBooks"
                style={styles.tileIcon}
            />
            <h2 style={styles.tileTitle}>Connect QuickBooks</h2>
            <p style={styles.tileText}>
                Link your QuickBooks account to streamline your accounts receivable process.
                Manage invoices, track payments, and automate collections.
            </p>
            <button 
                style={{
                    ...styles.connectButton, 
                }}
                disabled={quickbooksLoading}
                onClick={handleQuickbooksConnection}
            >
                {quickbooksLoading ? "Connecting..." : 
                quickbooksConnected ? "Connected ✓" : "Connect QuickBooks"}
            </button>
        </div>

        {/* MYOB Tile */}
        <div style={styles.tile}>
            <img 
                src={UtilityFunctions.getImageURL() + "myob.png"} 
                alt="Connect MYOB"
                style={styles.tileIcon}
            />
            <h2 style={styles.tileTitle}>Connect MYOB</h2>
            <p style={styles.tileText}>
                Link your MYOB account to streamline your accounts receivable process.
                Manage invoices, track payments, and automate collections.
            </p>
            <button 
                style={{...styles.connectButton}}
                onClick={handleMyobConnection}
                disabled={myobLoading}
            >
                {myobLoading ? "Connecting..." : "Connect MYOB"}
            </button>
        </div>

        {/* API Tile */}
        <div style={styles.tile}>
            <img 
                src={UtilityFunctions.getImageURL() + "api-logo.png"} 
                alt="Connect API"
                style={styles.tileIcon}
            />
            <h2 style={styles.tileTitle}>Connect API</h2>
            <p style={styles.tileText}>
                Link your accounting system to MPLOI Accounts Receivable to sync invoices, track payments, and automate collections.
            </p>
            <button 
                style={{...styles.connectButton}}
                onClick={handleMyobConnection}
                disabled={myobLoading}
            >
                {myobLoading ? "Connecting..." : "Connect API"}
            </button>
        </div>

        {/* Smokeball Tile */}
        <div style={styles.tile}>
            <img 
                src={UtilityFunctions.getImageURL() + "smokeball-logo.jpg"} 
                alt="Connect Smokeball"
                style={styles.tileIcon}
            />
            <h2 style={styles.tileTitle}>Connect Smokeball</h2>
            <p style={styles.tileText}>
                Link your Smokeball legal and account system to MPLOI Accounts Receivable to sync invoices, track payments, and automate collections.
            </p>
            <button 
                style={{...styles.connectButton}}
                onClick={handleMyobConnection}
                disabled={myobLoading}
            >
                {myobLoading ? "Connecting..." : "Connect Smokeball"}
            </button>
        </div>

        {/* Zoho Invoices Tile */}
        <div style={styles.tile}>
            <img 
                src={UtilityFunctions.getImageURL() + "zoho.png"}
                alt="Connect Zoho"
                style={styles.tileIcon}
            />
            <h2 style={styles.tileTitle}>Connect Zoho Invoices</h2>
            <p style={styles.tileText}>
                Connect your Zoho Invoices account to sync invoices, track payments, and automate collections.
            </p>
            <button 
                style={{...styles.connectButton}}
                onClick={handleZohoConnection}
                disabled={zohoLoading}
            >
                {zohoLoading ? "Connecting..." : "Connect Zoho Invoices"}
            </button>
        </div>

        {/* Zoho Books Tile */}
        <div style={styles.tile}>
            <img 
                src={UtilityFunctions.getImageURL() + "zoho.png"}
                alt="Connect Zoho"
                style={styles.tileIcon}
            />
            <h2 style={styles.tileTitle}>Connect Zoho Books</h2>
            <p style={styles.tileText}>
                Connect your Zoho Books account to sync invoices, track payments, and automate collections.
            </p>
            <button 
                style={{...styles.connectButton}}
                onClick={handleZohoBooksConnection}
                disabled={zohoBooksLoading}
            >
                {zohoBooksLoading ? "Connecting..." : "Connect Zoho Books"}
            </button>
        </div>

        {/* Dynamics 365 Business Central Tile */}
        <div style={styles.tile}>
            <img 
                src={UtilityFunctions.getImageURL() + "dynamics365.jpg"}
                alt="Connect Dynamics 365"
                style={styles.tileIcon}
            />
            <h2 style={styles.tileTitle}>Connect Dynamics 365 Business Central</h2>
            <p style={styles.tileText}>
                Connect your Dynamics 365 Business Central account to sync invoices, track payments, and automate collections.
            </p>
            <button 
                style={{...styles.connectButton}}
                onClick={handleDynamicsConnection}
                disabled={dynamicsLoading}
            >
                {dynamicsLoading ? "Connecting..." : "Connect Dynamics 365 Business Central"}
            </button>
        </div>
    </div>
);
    
const Dashboard = ({
    initialLoading,
    xeroConnected,
    quickbooksConnected,
    myobConnected,
    demoConnected,
    zohoConnected,
    zohoBooksConnected,
    dynamicsConnected,
    collectionsEnabled,
    needsXeroReconnect,
    globalOrganizationId,
    stats,
    loading,
    accounts,
    totalInvoices,
    invoicesPerPage,
    currentPage,
    searchTerm,
    handleSearchChange,
    handleSearchSubmit,
    handleClearSearch,
    handlePageChange,
    globalInteractions,
    initiateCall,
    setSelectedAccount,
    setTestCallPhone,
    setShowTestCallModal,
    userData,
    handleXeroConnection,
    history, // Pass props.history down as history
    activeFilter,
    handleFilterChange,
}) => {
        const totalPages = Math.ceil(totalInvoices / invoicesPerPage);
        const startInvoiceNum = totalInvoices > 0 ? (currentPage - 1) * invoicesPerPage + 1 : 0;
        const endInvoiceNum = Math.min(currentPage * invoicesPerPage, totalInvoices);

        // Helper to format customer name like in the image
        const formatCustomerName = (name) => {
            if (!name) return { main: 'Unknown Customer', sub: '' };
            const match = name.match(/^(.*?)\s*\(([^)]*)\)$/);
            if (match) {
                return { main: match[1].trim(), sub: `(${match[2]})` };
            }
            return { main: name.trim(), sub: '' };
        };

        // Helper to check for missing phone
        const isPhoneMissing = (phone) => {
            return !phone || phone.trim() === '' || phone.toLowerCase() === 'no phone provided' || phone.toLowerCase() === 'n/a';
        };

        return (
        <div style={{width: '100%'}}> 
            {/* Collections Disabled Banner */}
            {!initialLoading && (xeroConnected || quickbooksConnected || myobConnected || demoConnected || zohoConnected || zohoBooksConnected || dynamicsConnected) && !collectionsEnabled && (
                <div style={{ marginBottom: '24px' }}>
                    <div style={styles.collectionsDisabledBanner}>
                        <p style={styles.bannerText}>
                            <span role="img" aria-label="warning" style={{fontSize: '20px'}}>⚠️</span>
                            Collections are currently disabled. Enable collections to start managing overdue invoices.
                        </p>
                        <button 
                            style={{...styles.button, ...styles.enableButton}}
                            onClick={() => history.push(`/ar/settings/${globalOrganizationId}`)}
                        >
                            Enable Collections
                        </button>
                    </div>
                </div>
            )}

            {/* Stats Cards Row - Remains above the two-column layout */}
            {!initialLoading && (xeroConnected || quickbooksConnected || myobConnected || demoConnected || zohoConnected || zohoBooksConnected || dynamicsConnected) && (
                <div className="row mb-4"> 
                    {/* Tile 1: Calls Today */}
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3"> {/* Changed col-xl-4 to col-xl-3, added col-lg-6 for better responsiveness */}
                        <div style={styles.blueBox}>
                            <div style={styles.statsCard}>
                                <div style={styles.statsContent}>
                                    <h4 style={{...styles.mutedText, fontSize: '14px', marginBottom: '5px'}}>Calls Today</h4>
                                    <h2 style={{...styles.whiteText, fontSize: '28px', fontWeight: 'bold'}}>{stats.callsToday}</h2>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tile 2: Outstanding */}
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3"> {/* Changed col-xl-4 to col-xl-3, added col-lg-6 */}
                        <div style={styles.blueBox}>
                            <div style={styles.statsCard}>
                                <div style={styles.statsContent}>
                                    <h4 style={{...styles.mutedText, fontSize: '14px', marginBottom: '5px'}}>Outstanding</h4>
                                    <h2 style={{...styles.whiteText, fontSize: '28px', fontWeight: 'bold'}}>
                                        ${stats.totalOutstanding?.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        }) || '0.00'}
                                    </h2>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tile 3: Total Invoices Collected */}
                    {/* Ensure this 'stats.totalInvoicesCollected' exists in your stats object and is populated */}
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3"> {/* Changed col-xl-4 to col-xl-3, added col-lg-6 */}
                        <div style={styles.blueBox}>
                            <div style={styles.statsCard}>
                                <div style={styles.statsContent}>
                                    <h4 style={{...styles.mutedText, fontSize: '14px', marginBottom: '5px'}}>Invoices Collected by MPLOI</h4>
                                    <h2 style={{...styles.whiteText, fontSize: '28px', fontWeight: 'bold'}}>
                                        {stats.totalInvoicesCollected || '0'} 
                                    </h2>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Tile 4: Total Collected by MPLOI */}
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3"> {/* Changed col-xl-4 to col-xl-3, added col-lg-6 */}
                        <div style={styles.blueBox}>
                            <div style={styles.statsCard}>
                                <div style={styles.statsContent}>
                                    <h4 style={{...styles.mutedText, fontSize: '14px', marginBottom: '5px'}}>Amount Collected by MPLOI</h4>
                                    <h2 style={{...styles.whiteText, fontSize: '28px', fontWeight: 'bold'}}>
                                        ${stats.totalCollected?.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        }) || '0.00'}
                                    </h2>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Two-Column Layout */}
            {loading && !accounts.length && !initialLoading ? ( 
                <div style={{...styles.emptyState, color: '#fff', minHeight: '300px', fontSize: '18px'}}>
                    <div className="spinner-border text-light" style={{width: '3rem', height: '3rem'}} role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                    <p style={{marginTop: '20px'}}>Fetching data...</p>
                </div>
            ) : (xeroConnected || quickbooksConnected || myobConnected || demoConnected || zohoConnected || zohoBooksConnected || dynamicsConnected) && !initialLoading ? (
                <div style={styles.dashboardContent}>
                    
                    {/* Left Column: Outstanding Invoices */}
                    <div style={styles.leftColumn}>
                        <div style={styles.blueBox}>
                            {(xeroConnected || quickbooksConnected || myobConnected || demoConnected || zohoConnected || zohoBooksConnected || dynamicsConnected) && !initialLoading && (
                                <>
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center' }}> 
                                    <input
                                        type="text"
                                        placeholder="Search invoices by customer, invoice #..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        style={{ ...styles.searchInput, flexGrow: 1 }} // Allow input to grow
                                    />
                                    <button
                                        onClick={handleSearchSubmit}
                                        style={{ ...styles.button, ...styles.primaryButton, padding: '12px 18px' }} // Adjusted padding to better match typical input height
                                        disabled={loading}
                                    >
                                        Search
                                    </button>
                                    {searchTerm && ( // Only show Clear button if there's a search term
                                        <button
                                            onClick={handleClearSearch}
                                            style={{ ...styles.button, ...styles.secondaryButton, padding: '12px 18px' }} // Adjusted padding
                                            disabled={loading}
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                                    <button
                                        onClick={() => handleFilterChange('enabled')}
                                        style={{...styles.button, ...(activeFilter === 'enabled' ? styles.primaryButton : styles.secondaryButton)}}
                                        disabled={loading}
                                    >
                                        Enabled
                                    </button>
                                    <button
                                        onClick={() => handleFilterChange('disabled')}
                                        style={{...styles.button, ...(activeFilter === 'disabled' ? styles.primaryButton : styles.secondaryButton)}}
                                        disabled={loading}
                                    >
                                        Disabled
                                    </button>
                                    <button
                                        onClick={() => handleFilterChange('next_to_call')}
                                        style={{...styles.button, ...(activeFilter === 'next_to_call' ? styles.primaryButton : styles.secondaryButton)}}
                                        disabled={loading}
                                    >
                                        Next to Call
                                    </button>
                                </div>
                                </>
                            )}
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                                <h3 style={{...styles.whiteText, fontSize: '22px', fontWeight: '600'}}>
                                    Outstanding Invoices ({totalInvoices})
                                </h3>
                            </div>
                            <div className="table-responsive">
                                <table style={styles.table}>
                                    <thead>
                                        <tr style={{backgroundColor: 'transparent'}}> 
                                            <th style={{...styles.tableHeader, width: '25%'}}>Customer</th>
                                            <th style={{...styles.tableHeader, width: '15%', textAlign: 'center'}}>Inv</th>
                                            <th style={{...styles.tableHeader, width: '15%', textAlign: 'right'}}>Amount</th>
                                            <th style={{...styles.tableHeader, width: '15%', textAlign: 'center'}}>Due</th>
                                            <th style={{...styles.tableHeader, width: '10%', textAlign: 'center'}}>Status</th>
                                            <th style={{...styles.tableHeader, width: '15%', textAlign: 'center'}}>Last</th>
                                            <th style={{...styles.tableHeader, width: '15%', textAlign: 'center'}}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {accounts.length > 0 ? accounts.map(account => {
                                            const customer = formatCustomerName(account.customerDisplayName);
                                            const phoneIsMissing = isPhoneMissing(account.phone);
                                            return (
                                                <tr key={account.id} style={styles.tableRow}>
                                                    <td style={{...styles.tableCell, borderTopLeftRadius: '6px', borderBottomLeftRadius: '6px'}}>
                                                        <div style={{fontWeight: '500'}}>
                                                            {customer.main}
                                                            {phoneIsMissing && (
                                                                <span style={styles.missingInfoTag} title="Customer phone number is missing">📞 No Phone</span>
                                                            )}
                                                        </div>
                                                        {customer.sub && <div style={{fontSize: '11px', color: 'rgba(224,224,224,0.5)'}}>{customer.sub}</div>}
                                                    </td>
                                                    <td style={{...styles.tableCell, textAlign: 'center'}}>{account.invoiceNumber}</td>
                                                    <td style={{...styles.tableCell, textAlign: 'right'}}>${account.amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                                    <td style={{...styles.tableCell, textAlign: 'center'}}>
                                                        {moment(account.dueDate).format('DD/MM/YYYY')}
                                                    </td>
                                                    <td style={{...styles.tableCell, textAlign: 'center'}}>
                                                        <span style={{
                                                            ...styles.badge,
                                                            backgroundColor: account.overdue_flags?.daysOverdue > 60 ? '#dc3545' : 
                                                                            account.overdue_flags?.daysOverdue > 30 ? '#ffc107' : 
                                                                            account.overdue_flags?.daysOverdue > 0 ? '#17a2b8' :  
                                                                            '#28a745', 
                                                            color: account.overdue_flags?.daysOverdue > 30 && account.overdue_flags?.daysOverdue <=60 ? '#212529' : '#ffffff' 
                                                        }}>
                                                            {account.overdue_flags?.daysOverdue > 0 ? 
                                                                `${account.overdue_flags?.daysOverdue} days` : 
                                                                'Current'}
                                                        </span>
                                                    </td>
                                                    <td style={{...styles.tableCell, textAlign: 'center'}}>
                                                        {account.lastContact ? 
                                                            moment(account.lastContact).local().format('DD/MM/YY HH:mm') : 
                                                            'Never'}
                                                    </td>
                                                    <td style={{...styles.tableCell, textAlign: 'center', borderTopRightRadius: '6px', borderBottomRightRadius: '6px'}}>
                                                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                                            <button 
                                                                title="Call Customer"
                                                                style={{...styles.button, ...styles.primaryButton, ...styles.actionButton}}
                                                                onClick={() => initiateCall(account.organizationId, account.contactId, account.id)}
                                                                disabled={phoneIsMissing} // Optionally disable call if phone is missing
                                                            >
                                                                Call
                                                            </button>
                                                            <button 
                                                                title="Test Call"
                                                                style={{...styles.button, ...styles.secondaryButton, ...styles.actionButton}}
                                                                onClick={() => {
                                                                    setSelectedAccount(account);
                                                                    setTestCallPhone(userData?.user?.mobileNumber || ''); 
                                                                    setShowTestCallModal(true);
                                                                }}
                                                            >
                                                                Test
                                                            </button>
                                                            <button 
                                                                title="View Details"
                                                                style={{...styles.button, ...styles.secondaryButton, ...styles.actionButton}}
                                                                onClick={() => history.push(`/ar/view/${account.organizationId}/${account.contactId}`)}
                                                            >
                                                                View
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) 
                                        }) 
                                        : ( 
                                            <tr>
                                                <td colSpan="7" style={{...styles.tableCell, textAlign: 'center', padding: '40px 20px', color: 'rgba(255,255,255,0.5)'}}>
                                                    {loading ? 'Searching...' : (searchTerm ? 'No invoices match your search criteria.' : 'No outstanding invoices found.')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {/* --- PAGINATION CONTROLS --- */}
                            {( 
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginTop: '20px',
                                    padding: '10px 0', 
                                    color: 'rgba(255,255,255,0.7)',
                                    fontSize: '13px'
                                }}>
                                    <div>
                                        Showing {startInvoiceNum} - {endInvoiceNum} of {totalInvoices} invoices
                                    </div>
                                    <div style={{display: 'flex', gap: '6px'}}>
                                        <button 
                                            onClick={() => handlePageChange(1)} 
                                            disabled={currentPage === 1 || loading}
                                            style={{...styles.button, ...styles.secondaryButton, ...styles.paginationButton}}
                                        >
                                            First
                                        </button>
                                        <button 
                                            onClick={() => handlePageChange(currentPage - 1)} 
                                            disabled={currentPage === 1 || loading}
                                            style={{...styles.button, ...styles.secondaryButton, ...styles.paginationButton}}
                                        >
                                            Prev
                                        </button>
                                        <span style={{padding: '5px 10px', alignSelf: 'center', backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '4px'}}>
                                            {currentPage} / {totalPages}
                                        </span>
                                        <button 
                                            onClick={() => handlePageChange(currentPage + 1)} 
                                            disabled={currentPage === totalPages || loading}
                                            style={{...styles.button, ...styles.secondaryButton, ...styles.paginationButton}}
                                        >
                                            Next
                                        </button>
                                        <button 
                                            onClick={() => handlePageChange(totalPages)} 
                                            disabled={currentPage === totalPages || loading}
                                            style={{...styles.button, ...styles.secondaryButton, ...styles.paginationButton}}
                                        >
                                            Last
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Recent Activity */}
                    <div style={styles.rightColumn}>
                        <div style={styles.blueBox}>
                            <h3 style={{...styles.whiteText, fontSize: '22px', fontWeight: '600', marginBottom: '20px'}}>
                                <span style={{marginRight: '10px', verticalAlign: 'middle'}}>📋</span>
                                Recent Activity
                            </h3>
                            <div style={{...styles.timeline, maxHeight: 'calc(100vh - 350px)', overflowY: 'auto', paddingRight: '10px'}}> 
                                {globalInteractions.length > 0 ? globalInteractions.map((interaction, index) => (
                                    <div key={interaction.id || index} style={{
                                        ...styles.timelineItem, 
                                        borderLeft: `2px solid ${index === globalInteractions.length -1 && globalInteractions.length > 1 ? 'transparent' : 'rgba(255, 255, 255, 0.1)'}`
                                        }}>
                                        <div style={{
                                            ...styles.timelineDot,
                                            backgroundColor: interaction.type === 'call' 
                                                ? getOutcomeColor(interaction.outcome)
                                                : '#28a745' 
                                        }} />
                                        
                                        <div style={{marginBottom: '10px'}}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                marginBottom: '5px'
                                            }}>
                                                <div>
                                                    <h4 style={{...styles.whiteText, fontSize: '14px', fontWeight: '500', marginBottom: '4px'}}>
                                                        {interaction.type === 'call' ? (
                                                            <>
                                                                <span style={{fontSize: '14px'}}>{interaction.direction === 'outbound' ? '📤' : '📥'}</span> &nbsp;
                                                                {interaction.direction === 'outbound' ? 'Outbound' : 'Inbound'} Call
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span style={{fontSize: '14px'}}>💵</span> &nbsp;Payment Received
                                                            </>
                                                        )}
                                                        {interaction.customer?.name && interaction.customer?.id ? (
                                                            <>
                                                            {' - '}
                                                            <Link 
                                                                to={`/ar/view/${interaction.customer.organizationId}/${interaction.customer.id}`}
                                                                style={{color: '#87CEFA', textDecoration: 'none', fontWeight: 'normal'}} 
                                                            >
                                                                {interaction.customer.name}
                                                            </Link>
                                                            </>
                                                        ) : (interaction.customer?.name ? ` - ${interaction.customer.name}`: '')}
                                                    </h4>
                                                    <p style={{...styles.mutedText, fontSize: '12px', margin: 0}}>
                                                        {moment(interaction.timestamp).local().format('DD/MM/YY HH:mm')}
                                                    </p>
                                                </div>
                                                {interaction.type === 'call' && interaction.outcome && (
                                                    <span style={{
                                                        ...styles.badge,
                                                        padding: '3px 8px', fontSize: '10px',
                                                        backgroundColor: getOutcomeColor(interaction.outcome),
                                                        color: ['#ffc107', '#f8f9fa'].includes(getOutcomeColor(interaction.outcome)) ? '#212529' : '#ffffff', 
                                                        alignSelf: 'flex-start',
                                                        marginLeft: '8px',
                                                        flexShrink: 0, 
                                                    }}>
                                                        {capitalizeFirst(interaction.outcome)}
                                                    </span>
                                                )}
                                            </div>

                                            {interaction.type === 'call' ? (
                                                <>
                                                    {interaction.duration > 0 && <p style={{...styles.whiteText, fontSize: '13px', margin: '4px 0'}}>
                                                        Duration: {formatDuration(interaction.duration)}
                                                    </p>}
                                                    <p style={{...styles.mutedText, fontSize: '13px', lineHeight: '1.5', margin: '4px 0', wordBreak: 'break-word'}}>
                                                        {interaction.summary || "No summary."}
                                                    </p>
                                                </>
                                            ) : (
                                                <>
                                                    <p style={{...styles.whiteText, fontSize: '13px', margin: '4px 0'}}>
                                                        Amount: ${interaction.amount?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                                                    </p>
                                                    {interaction.invoice && <p style={{...styles.mutedText, fontSize: '13px', margin: '4px 0'}}>
                                                        Invoice: {interaction.invoice}
                                                    </p>}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )) : (
                                    <div style={{
                                        padding: '30px 0',
                                        textAlign: 'center',
                                        color: 'rgba(255,255,255,0.5)'
                                    }}>
                                        {loading ? 'Loading activity...' : (searchTerm ? 'No activity matches your search.' : 'No recent activity.')}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : !initialLoading && !(xeroConnected || quickbooksConnected || myobConnected || demoConnected || zohoConnected || zohoBooksConnected) ? (
                <EmptyState
                    loading={loading}
                    handleXeroConnection={handleXeroConnection}
                    quickbooksLoading={quickbooksLoading}
                    handleQuickbooksConnection={handleQuickbooksConnection}
                    quickbooksConnected={quickbooksConnected}
                    myobLoading={myobLoading}
                    handleMyobConnection={handleMyobConnection}
                    zohoLoading={zohoLoading}                 // <-- Add this line
                    handleZohoConnection={handleZohoConnection} // <-- And this one
                    zohoBooksLoading={zohoBooksLoading} // <-- Add this line
                    handleZohoBooksConnection={handleZohoBooksConnection} // <-- And this one
                    zohoBooksConnected={zohoBooksConnected} // <-- Add this line
                    dynamicsLoading={dynamicsLoading} // <-- Add this line
                    handleDynamicsConnection={handleDynamicsConnection} // <-- And this one
                    dynamicsConnected={dynamicsConnected} // <-- Add this line
                />
            ) : null } 
        </div>
    );
};

const AccountsReceivable = (props) => {
    const LOCAL_STORAGE_KEYS = {
      AR_CURRENT_PAGE: 'mploiArCurrentPage', // Prefixed to avoid collisions
      AR_SEARCH_TERM: 'mploiArSearchTerm',
    };
    // Redux setup
    const dispatch = useDispatch();
    const reduxState = useSelector((state) => state);
    const { setUnreadNotificationCount } = bindActionCreators(actionCreators, dispatch);
    

    // Loading states
    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [myobLoading, setMyobLoading] = useState(false);
    const [quickbooksLoading, setQuickbooksLoading] = useState(false);
    
    // Data loading tracking
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const [userDataLoaded, setUserDataLoaded] = useState(false);
    const [subscriptionDataLoaded, setSubscriptionDataLoaded] = useState(false);
    
    // User and authentication
    const [userData, setUserData] = useState(null);
    const user = cookieService?.getCookie(STRING_CONSTANTS?.cookieStrings?.user);
    const isUserLoggedIn = cookieService.getCookie(STRING_CONSTANTS.cookieStrings.token);
    
    // Connection states
    const [xeroConnected, setXeroConnected] = useState(false);
    const [quickbooksConnected, setQuickbooksConnected] = useState(false);
    const [myobConnected, setMyobConnected] = useState(false);
    const [demoConnected, setDemoConnected] = useState(false);
    const [isDemoMode, setIsDemoMode] = useState(false);
    const [demoOrgDisplayInfo, setDemoOrgDisplayInfo] = useState(null);

    const [outstandingInvoicesCount, setOutstandingInvoicesCount] = useState(0);

    const [zohoLoading, setZohoLoading] = useState(false);
    const [zohoConnected, setZohoConnected] = useState(false);

    const [zohoBooksLoading, setZohoBooksLoading] = useState(false);
    const [zohoBooksConnected, setZohoBooksConnected] = useState(false);
    
    const [dynamicsLoading, setDynamicsLoading] = useState(false);
    const [dynamicsConnected, setDynamicsConnected] = useState(false);

    // Organization and data
    const [globalOrganizationId, setGlobalOrganizationId] = useState(null);
    const [collectionsEnabled, setCollectionsEnabled] = useState(false);
    const [needsXeroReconnect, setNeedsXeroReconnect] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [invoiceData, setInvoiceData] = useState(initialInvoiceDataState);
    const [stats, setStats] = useState(initialStatsState);
    const [globalInteractions, setGlobalInteractions] = useState([]);

    const [currentPage, setCurrentPage] = useState(() => {
        const savedPage = localStorage.getItem(LOCAL_STORAGE_KEYS.AR_CURRENT_PAGE);
        return savedPage ? parseInt(savedPage, 10) : 1;
    });
    const [searchTerm, setSearchTerm] = useState(() => {
        const savedSearchTerm = localStorage.getItem(LOCAL_STORAGE_KEYS.AR_SEARCH_TERM);
        return savedSearchTerm || '';
    });
    const [activeFilter, setActiveFilter] = useState(null);

    const [invoicesPerPage] = useState(10); // Or your preferred number, e.g., 6-7 to match image
    const [totalInvoices, setTotalInvoices] = useState(0);

    // Subscription and payment
    const [isPaid, setIsPaid] = useState(false);
    const [subsModalAccounts, setSubsModalAccounts] = useState(false);
    
    // Test call modal
    const [showTestCallModal, setShowTestCallModal] = useState(false);
    const [testCallPhone, setTestCallPhone] = useState('');
    const [selectedAccount, setSelectedAccount] = useState(null);
    
    // MYOB specific
    const [showMyobAuthModal, setShowMyobAuthModal] = useState(false);
    const [selectedCompanyFiles, setSelectedCompanyFiles] = useState([]);
    const [companyFileCredentials, setCompanyFileCredentials] = useState({});
    
    const [demoOrgIdFromQuery, setDemoOrgIdFromQuery] = useState(null);

    // Effect to save currentPage to localStorage
    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEYS.AR_CURRENT_PAGE, currentPage.toString());
    }, [currentPage]);

    // Effect to save searchTerm to localStorage
    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEYS.AR_SEARCH_TERM, searchTerm);
    }, [searchTerm]);

    // Add this effect at the top of your component
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const demoOrgIdFromQuery  = urlParams.get('demo_org_id');
        // This effect runs only once on mount
        if (demoOrgIdFromQuery) {
            setDemoOrgIdFromQuery(demoOrgIdFromQuery);
            setIsDemoMode(true);
        }
    }, []); // Empty dependency array = run once on mount

    const handleSearchChange = useCallback((event) => {
        setSearchTerm(event.target.value);
    }, []); // Empty dependency array makes the function reference stable

    const handleSearchSubmit = async () => {
        if (globalOrganizationId) {
            setLoading(true);
            setCurrentPage(1); // Reset to first page on new search
            // Use the current searchTerm from state
            await fetchInvoiceData(globalOrganizationId, 1, invoicesPerPage, searchTerm, activeFilter);
            await fetchGlobalInteractionsForOrg(globalOrganizationId);
            // setLoading(false) is typically handled within fetchInvoiceData/fetchGlobalInteractionsForOrg
        }
    };

    const handleClearSearch = async () => {
        if (globalOrganizationId) {
            setLoading(true);
            setSearchTerm(''); // Clear the search term in state
            setCurrentPage(1); // Reset to first page
            await fetchInvoiceData(globalOrganizationId, 1, invoicesPerPage, '', activeFilter); // Fetch with empty search term
            await fetchGlobalInteractionsForOrg(globalOrganizationId); // Fetch with empty search term
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= Math.ceil(totalInvoices / invoicesPerPage) && newPage !== currentPage) {
            fetchInvoiceData(globalOrganizationId, newPage, invoicesPerPage, searchTerm);
        }
    };

    const handleFilterChange = async (filterType) => {
        if (globalOrganizationId) {
            setLoading(true);
            // This allows clicking the same filter again to turn it off
            const newFilter = activeFilter === filterType ? null : filterType;
            setActiveFilter(newFilter);
            setCurrentPage(1); // Reset to the first page
            await fetchInvoiceData(globalOrganizationId, 1, invoicesPerPage, searchTerm, newFilter);
        }
    };

    // Fetch global interactions for an organization
    const fetchGlobalInteractionsForOrg = useCallback(async (organizationIdToFetch) => {
        if (!organizationIdToFetch) {
            console.log("[fetchGlobalInteractionsForOrg] No organizationId provided or component unmounted, clearing interactions.");
            setGlobalInteractions([]);
            return;
        }
        
        try {
            const response = await userService.aiRequest({
                method: 'get-global-interactions',
                data: { limit: 30, organizationId: organizationIdToFetch },
            }, true);

            if (response.data?.interactions) {
                const gi = response.data.interactions.map(interaction => ({
                    id: interaction.id,
                    type: interaction.type,
                    direction: interaction.direction,
                    timestamp: interaction.timestamp,
                    duration: interaction.duration || 0,
                    outcome: interaction.outcome,
                    summary: interaction.summary,
                    recordingUrl: interaction.recording_url,
                    transcript: interaction.transcript,
                    amount: interaction.amount,
                    method: interaction.method,
                    reference: interaction.reference,
                    invoice: interaction.relatedInvoices?.[0]?.invoiceNumber || null,
                    customer: {
                        id: interaction.contactId || interaction.xeroContactId,
                        organizationId: interaction.organizationId,
                        name: interaction.customer_name || interaction.customerName || 'Unknown Customer',
                        email: interaction.customer_email || interaction.customerEmail,
                        phone: interaction.customer_phone || interaction.customerPhone
                    }
                }));
                setGlobalInteractions(gi);
            } else {
                setGlobalInteractions([]);
            }
        } catch (error) {
            console.error('Error fetching global interactions:', error);
            setGlobalInteractions([]);
        }
    }, []);
    
    // Initiate a call
    const initiateCall = async (organizationId, contactId, invoiceId = null) => {
        try {
            const response = await userService.aiRequest({
                method: 'initiate-call',
                data: {
                    organizationId,
                    contactId,
                    invoiceId,
                }
            }, true);

            if (response.data?.success) {
                SuccessToast("Success", "Call successfully initiated.");
            } else {
                ValidationErrorToast("Error", `Call could not be initiated: ${response.data?.error || 'Unknown error'}`);
            }
        } catch (error) {
            ValidationErrorToast("Error", `Call could not be initiated: ${error.message || 'Unknown error'}`);
        }
    };
    
    // Handle test call
    const handleTestCall = async () => {
        if (!testCallPhone) {
            ValidationErrorToast("Error", "Please enter a phone number");
            return;
        }
    
        try {
            const response = await userService.aiRequest({
                method: 'initiate-call',
                data: {
                    organizationId: selectedAccount.organizationId,
                    contactId: selectedAccount.contactId,
                    invoiceId: selectedAccount.id,
                    testPhone: testCallPhone,
                    isTest: true
                }
            }, true);
    
            if (response.data?.success) {
                SuccessToast("Success", "Test call initiated successfully");
                setShowTestCallModal(false);
                setTestCallPhone('');
                setSelectedAccount(null);
            } else {
                ValidationErrorToast("Error", `Call could not be initiated: ${response.data?.error || 'Unknown error'}`);
            }
        } catch (error) {
            ValidationErrorToast("Error", `Call could not be initiated: ${error.message || 'Unknown error'}`);
        }
    };
    
    // Handle connection functions
    const handleXeroConnection = async () => {
        try {
            setLoading(true);
            const account_id = user.id;
            if (window.location.host.includes('localhost')) {
                window.location.href = `http://localhost:5000/connect/${account_id}`;
            } else {
                window.location.href = `https://ai.mploi.com/connect/${account_id}`;
            }
        } catch (error) {
            ValidationErrorToast("Error", "Failed to connect to Xero");
            setLoading(false);
        }
    };

    const handleQuickbooksConnection = async () => {
        try {
            setQuickbooksLoading(true);
            const account_id = user.id;
            if (window.location.host.includes('localhost')) {
                window.location.href = `http://localhost:5000/connect-quickbooks/${account_id}`;
            } else {
                window.location.href = `https://ai.mploi.com/connect-quickbooks/${account_id}`;
            }
        } catch (error) {
            ValidationErrorToast("Error", "Failed to connect to QuickBooks");
            setQuickbooksLoading(false);
        }
    };

    const handleZohoConnection = async () => {
        try {
            setZohoLoading(true);
            const account_id = user.id;
            if (window.location.host.includes('localhost')) {
                window.location.href = `http://localhost:5000/connect-zoho/${account_id}`;
            } else {
                window.location.href = `https://ai.mploi.com/connect-zoho/${account_id}`;
            }
        } catch (error) {
            ValidationErrorToast("Error", "Failed to connect to Zoho");
            setZohoLoading(false);
        }
    };

    const handleZohoBooksConnection = async () => {
    try {
            setZohoBooksLoading(true);
            const account_id = user.id;
            if (window.location.host.includes('localhost')) {
                window.location.href = `http://localhost:5000/connect-zoho-books/${account_id}`;
            } else {
                window.location.href = `https://ai.mploi.com/connect-zoho-books/${account_id}`;
            }
        } catch (error) {
            ValidationErrorToast("Error", "Failed to connect to Zoho Books");
            setZohoBooksLoading(false);
        }
    };

    const handleDynamicsConnection = async () => {
    try {
            setDynamicsLoading(true);
            const account_id = user.id;
            if (window.location.host.includes('localhost')) {
                window.location.href = `http://localhost:5000/connect-dynamics/${account_id}`;
            } else {
                window.location.href = `https://ai.mploi.com/connect-dynamics/${account_id}`;
            }
        } catch (error) {
            ValidationErrorToast("Error", "Failed to connect to Dynamics 365 Business Central");
            setDynamicsLoading(false);
        }
    };

    const handleMyobConnection = async () => {
        try {
            setMyobLoading(true);
            const account_id = user.id;
            if (window.location.host.includes('localhost')) {
                window.location.href = `http://localhost:5000/myob-connect/${account_id}`;
            } else {
                window.location.href = `https://ai.mploi.com/myob-connect/${account_id}`;
            }
        } catch (error) {
            ValidationErrorToast("Error", "Failed to connect to MYOB");
            setMyobLoading(false);
        }
    };
    
    // Handle logout
    const handleLogout = (e) => {
        e.preventDefault();
        userService
          .logout()
          .then((res) => {
            api
              .deleteApi("device-token/", {
                deviceType: "WEB",
                token: `${cookieService.getCookie("fcm")}`,
              })
              .then((res) => {
                cookieService.removeAllCookies();
                props.history.push(STRING_CONSTANTS.navigationString.login);
              })
              .catch((e) => {
                cookieService.removeAllCookies();
                props.history.push(STRING_CONSTANTS.navigationString.login);
              });
          })
          .catch((err) => {
            checkData(props, "/", err);
          });
    };
    
    // Handle phone number change for test call
    const handlePhoneChange = useCallback((e) => {
        setTestCallPhone(e.target.value);
    }, []);

    // Fetch user data
    useEffect(() => {
        // Skip user data fetch if in demo mode or already loaded
        if (isDemoMode || userDataLoaded || !isUserLoggedIn) return;
        
        userService.getUserData()
            .then(res => {

                if (res && res.user) {
                    cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, res);
                    setUnreadNotificationCount(res.user.unreadNotificationCount);
                    setUserData(res);
                    setTestCallPhone(res.user.mobileNumber || '');
                } else {
                    console.warn("User data fetch response was not as expected:", res);
                }
            })
            .catch(err => {
                console.error("Error fetching user data:", err);
            })
            .finally(() => {
                setUserDataLoaded(true);
            });
    }, [isUserLoggedIn]); // Added isDemoMode to dependencies
            
    
        useEffect(() => {
            if (initialDataLoaded) return;
            setInitialDataLoaded(true);

            const urlParams = new URLSearchParams(window.location.search);
            const demoOrgId  = urlParams.get('demo_org_id');

            // Set isDemoMode early based on URL parameter
            if (demoOrgId) {
                setIsDemoMode(true);
            }
            
            const loadDashboardData = async () => {
                setInitialLoading(true);
                setLoading(true);
        
                // Reset all connection-specific states
                setXeroConnected(false);
                setQuickbooksConnected(false);
                setMyobConnected(false);
                setDemoConnected(false);
                setZohoConnected(false);
                setZohoBooksConnected(false);
                setDynamicsConnected(false);
                // Don't reset isDemoMode here since we set it above
                setGlobalOrganizationId(null);
                setDemoOrgDisplayInfo(null);
                setAccounts([]);
                setInvoiceData(initialInvoiceDataState);
                setStats(initialStatsState);
                setGlobalInteractions([]);
                setCollectionsEnabled(false);
        
                try {
                    if (demoOrgId) {
                        // Demo mode logic
                        await loadDemoData();
                    } else if (user?.id) {
                        // Standard user mode logic
                        console.log("USER ID:", user.id);
                        await loadUserData();
                    } else {
                        // Not logged in and no demo ID query param
                        console.log("[loadDashboardData] No user and no demo_org_id. Dashboard will be empty or redirect.");
                        setIsDemoMode(false);
                    }
                } catch (error) {
                    console.error("Error in loadDashboardData:", error);
                } finally {
                    setInitialLoading(false);
                    setLoading(false);
                    setInitialDataLoaded(true);
                }
            };
            
            // Demo data loading function
            const loadDemoData = async () => {
                const urlParams = new URLSearchParams(window.location.search);
                const demoOrgId  = urlParams.get('demo_org_id');
                console.log(`[loadDashboardData] DEMO MODE: Initializing for demo_org_id: ${demoOrgId}`);
                
                setIsDemoMode(true);
                
                try {
                    const response = await userService.aiRequest({
                        method: 'get-specific-demo-details',
                        data: { demo_organization_id: demoOrgId }
                    }, true);
    
                    if (response.data?.connected && response.data?.organization) {
                        const orgData = response.data.organization;
                        const fetchedInvoiceData = response.data.invoiceData;
    
                        setDemoConnected(true);
                        setGlobalOrganizationId(orgData.organizationId);
                        setDemoOrgDisplayInfo({ 
                            name: orgData.organizationName, 
                            affiliate_email: orgData.affiliateEmail 
                        });
                        setCollectionsEnabled(orgData.collectionsEnabled || false);
                        
                        if (fetchedInvoiceData) {
                            setInvoiceData(fetchedInvoiceData);
                            setStats(fetchedInvoiceData.summary?.stats || initialStatsState);
                            
                            const mappedAccounts = (fetchedInvoiceData.invoices || []).map(invoice => ({
                                id: invoice.invoiceID,
                                organizationId: invoice.organizationId,
                                contactId: invoice.xeroContactId,
                                customerName: invoice.contact?.name || 'Demo Customer',
                                invoiceNumber: invoice.invoiceNumber,
                                amount: parseFloat(invoice.amountDue || 0),
                                dueDate: invoice.overdueFlags?.dueDate,
                                status: invoice.status,
                                email: invoice.contact?.emailAddress || 'N/A',
                                phone: invoice.contact?.phones?.[0]?.phoneNumber || 'N/A',
                                lastContact: invoice.lastContact || null,
                                overdue_flags: invoice.overdueFlags || {},
                                call_history: invoice.interactionHistory || { 
                                    calls: [], 
                                    totalCalls: 0, 
                                    hasPaymentPlan: false, 
                                    latestOutcome: null 
                                },
                                source: 'demo'
                            }));
                            
                            setAccounts(mappedAccounts);
                        }
                        
                        if (orgData.organization_id) {
                            await fetchGlobalInteractionsForOrg(orgData.organization_id);
                        }
                    } else {
                        ValidationErrorToast(
                            "Demo Error", 
                            response.data?.message || "Could not load the specified demo account."
                        );
                        setIsDemoMode(false);
                    }
                } catch (error) {
                    console.error('Error loading demo account data:', error);
                    ValidationErrorToast(
                        "Demo Error", 
                        "Failed to load demo account data. Please check the ID or try again."
                    );
                    setIsDemoMode(false);
                }
            };
            
            // User data loading function
            const loadUserData = async () => {
                setIsDemoMode(false);
                
                try {
                    const account_id = user.id;
                    const results = await Promise.allSettled([
                        userService.aiRequest({ data: { account_id }, method: 'xero-check-connection' }, true),
                        userService.aiRequest({ data: { account_id }, method: 'quickbooks-check-connection' }, true),
                        userService.aiRequest({ data: { account_id }, method: 'myob-check-connection' }, true),
                        userService.aiRequest({ data: { account_id }, method: 'zoho-check-connection' }, true),
                        userService.aiRequest({ data: { account_id }, method: 'zoho-books-check-connection' }, true),
                        userService.aiRequest({ data: { account_id }, method: 'dynamics-check-connection' }, true)
                    ]);
                    
                    const xeroResult = results[0].status === 'fulfilled' ? results[0].value.data : null;
                    const quickbooksResult = results[1].status === 'fulfilled' ? results[1].value.data : null;
                    const myobResult = results[2].status === 'fulfilled' ? results[2].value.data : null;
                    const zohoResult = results[3].status === 'fulfilled' ? results[3].value.data : null;
                    const zohoBooksResult = results[4].status === 'fulfilled' ? results[4].value.data : null;
                    const dynamicsResult = results[5].status === 'fulfilled' ? results[5].value.data : null;
                    
                    let activeOrgId = null;
                    let activeCollectionsEnabled = false;
                    let activeSource = 'unknown';
    
                    if (xeroResult?.connected) {
                        setXeroConnected(true);
                        activeOrgId = xeroResult.organizationId;
                        if (xeroResult.needsReconnect) {
                            setNeedsXeroReconnect(true);
                        }
                        activeCollectionsEnabled = xeroResult.collectionsEnabled || false;
                        activeSource = 'xero';
                    }

                    if (zohoResult?.connected) {
                        setZohoConnected(true);
                        if (!activeOrgId) { 
                            activeOrgId = zohoResult.organizationId;
                            activeCollectionsEnabled = zohoResult.collectionsEnabled || false;
                            activeSource = 'zoho';
                            console.log("Setting activeOrgId to Zoho organizationId:", activeOrgId);
                        }
                    }

                    if (zohoBooksResult?.connected) {
                        setZohoBooksConnected(true);
                        if (!activeOrgId) { 
                            activeOrgId = zohoBooksResult.organizationId;
                            activeCollectionsEnabled = zohoBooksResult.collectionsEnabled || false;
                            activeSource = 'zohobooks';
                            console.log("Setting activeOrgId to Zoho Books organizationId:", activeOrgId);
                        }
                    }


                    if (quickbooksResult?.connected) {
                        setQuickbooksConnected(true);
                        if (!activeOrgId) {
                            activeOrgId = quickbooksResult.organizationId;
                            activeCollectionsEnabled = quickbooksResult.collectionsEnabled || false;
                            activeSource = 'quickbooks';
                        }
                    }
                    
                    if (myobResult?.connected) {
                        setMyobConnected(true);
                        if (!activeOrgId) {
                            activeOrgId = myobResult.organizationId;
                            activeCollectionsEnabled = myobResult.collectionsEnabled || false;
                            activeSource = 'myob';
                        }
                        
                        setSelectedCompanyFiles(myobResult.companyFiles || []);
                        if (myobResult.companyFiles?.some(f => f.requiresAuth && !f.connected)) {
                            setShowMyobAuthModal(true);
                        }
                    }

                    if (dynamicsResult?.connected) {
                        setDynamicsConnected(true);
                        if (!activeOrgId) {
                            activeOrgId = dynamicsResult.organizationId;
                            activeCollectionsEnabled = dynamicsResult.collectionsEnabled || false;
                            activeSource = 'dynamics';
                        }
                    }
                    
                    // This block now correctly uses the activeOrgId we just found
                    if (activeOrgId) {
                        setGlobalOrganizationId(activeOrgId);
                        setCollectionsEnabled(activeCollectionsEnabled);

                        await fetchInvoiceData(activeOrgId, currentPage, invoicesPerPage, searchTerm, activeFilter); 
                        await fetchGlobalInteractionsForOrg(activeOrgId);
                    }
                } catch (error) {
                    console.error('Error during standard connection checks:', error);
                }
            };
            // const urlParams = new URLSearchParams(window.location.search);
            // const demoOrgId  = urlParams.get('demo_org_id');

            if (user || demoOrgId) {
                loadDashboardData();

                const fetchDataChose = async () => {
                    try {
                        // Only dispatch the subscription fetch if not in demo mode
                        if (!isDemoMode) {
                            const res = await userService.aiRequest({
                                method: 'get-paid',
                                data: { email: user?.email }
                            }, true);
                            
                            if (res && res.data.isPaid) {
                                setSubsModalAccounts(false);
                                setIsPaid(true);
                            } else {
                                setSubsModalAccounts(true);
                                setIsPaid(false);
                            }
                        } else {
                            // In demo mode, just set these values directly
                            setSubsModalAccounts(false);
                            setIsPaid(true);
                        }
                    } catch (error) {
                        console.error("Subscription check error:", error);
                        setIsPaid(false);
                    } finally {
                        setSubscriptionDataLoaded(true);
                    }
                };
                
                fetchDataChose();
            } else if (!isUserLoggedIn && !demoOrgId) {
                setInitialLoading(false);
                setInitialDataLoaded(true);
            }
        }, [
            user, 
            isUserLoggedIn, 
            demoOrgIdFromQuery, 
            initialDataLoaded, 
            fetchGlobalInteractionsForOrg
        ]);

    // Process connection responses
    const processConnectionResponses = async (demoData, myobData, xeroData, quickbooksData) => {
        
        // Handle Demo connection
        const demoConnectionId = demoData?.organizationId;
        if (demoData?.connected) {
            setDemoConnected(true);
            setSelectedCompanyFiles(demoData.companyFiles);
        }

        // Handle MYOB connection
        const myobConnectionId = myobData?.organizationId;
        if (myobData?.connected) {
            setMyobConnected(true);
            setGlobalOrganizationId(myobConnectionId);
            setSelectedCompanyFiles(myobData.companyFiles);
            
            const needsAuth = myobData.companyFiles?.some(
                file => file.requiresAuth && !file.connected
            );
            if (needsAuth) {
                setShowMyobAuthModal(true);
            }
        }

        // Handle Xero connection
        const xeroOrganizationId = xeroData?.organizationId;
        if (xeroOrganizationId) {
            setXeroConnected(true);
            setGlobalOrganizationId(xeroOrganizationId);
            setCollectionsEnabled(xeroData.collectionsEnabled || false);
            
            // Only set subscription modal if not in demo mode
            // if (!isDemoMode) {
            //     if (!xeroData.paidAt) {
            //         console.log("Xero subscription not found, showing subscription modal!!!!");
            //         setSubsModalAccounts(false);
            //     } else {
            //         setSubsModalAccounts(false);
            //     }
            // }
        }

        // Handle QuickBooks connection
        const quickbooksOrganizationId = quickbooksData?.organizationId;
        if (quickbooksData?.connected) {
            setQuickbooksConnected(true);
            if (!xeroOrganizationId) {  // Use QuickBooks org if Xero isn't connected
                setGlobalOrganizationId(quickbooksOrganizationId);
                setCollectionsEnabled(quickbooksData.collectionsEnabled || false);
            }
        }

        // If any connection is active, fetch invoice data
        const activeOrganizationId = demoConnectionId || xeroOrganizationId || quickbooksOrganizationId || myobConnectionId;
        if (activeOrganizationId) {
            //await fetchInvoiceData(activeOrganizationId);
        } else {
            setXeroConnected(false);
            setQuickbooksConnected(false);
        }
    };

    // Fetch invoice data
    const fetchInvoiceData = async (organizationId, page = 1, limit = invoicesPerPage, currentSearchTerm = searchTerm, currentFilter = activeFilter) => {
            if (!organizationId) {
            setAccounts([]);
            setInvoiceData(initialInvoiceDataState);
            setTotalInvoices(0);
            setCurrentPage(1);
            setLoading(false);
            return;
        }
        setLoading(true);
        const offset = (page - 1) * limit;

        try {
            const invoiceResponse = await userService.aiRequest({
                data: { 
                    organization_id: organizationId, 
                    offset: offset, 
                    limit: limit,
                    search_term: currentSearchTerm,
                    filter: currentFilter,
                }, 
                method: 'get-account-invoices'
            }, true);

            if (invoiceResponse.data) {
                setInvoiceData(invoiceResponse.data); // Store the whole response
                setTotalInvoices(invoiceResponse.data.summary?.totalInvoices || 0);
                setCurrentPage(page);
                
                setStats({
                    callsToday: invoiceResponse.data.summary?.stats?.callsToday || 0,
                    totalOutstanding: invoiceResponse.data.summary?.stats?.totalOutstanding || 0,
                    // successfulCollections: invoiceResponse.data.summary?.stats?.successRate || 0, // Renamed in your state
                    successRate: invoiceResponse.data.summary?.stats?.successRate || 0,
                    totalCollected: invoiceResponse.data.summary?.stats?.totalCollected || 0,
                    totalInvoicesCollected: invoiceResponse.data.summary?.stats?.totalInvoicesCollected || 0,
                });

                const mappedAccounts = (invoiceResponse.data.invoices || []).map(invoice => ({
                    id: invoice.invoiceID || invoice.quickbooksInvoiceId,
                    organizationId: invoice.organizationId,
                    contactId: invoice.xeroContactId || invoice.quickbooksCustomerId,
                    customerName: invoice.contact?.name || 'Unknown Customer',
                    // For display purposes, let's try to match the image's customer format
                    customerDisplayName: invoice.contact?.name || 'Unknown Customer', // You might need to parse this further if it's complex
                    customerSubText: invoice.contact?.subText || '', // Example if you have sub-text
                    invoiceNumber: invoice.invoiceNumber || invoice.docNumber,
                    amount: parseFloat(invoice.amountDue || 0),
                    dueDate: invoice.overdueFlags?.dueDate,
                    status: invoice.Status || invoice.status, // Ensure this aligns with how you determine overdue status for badges
                    email: invoice.contact?.emailAddress || 'No email provided',
                    phone: invoice.contact?.phones?.[0]?.phoneNumber || 'No phone provided',
                    lastContact: invoice.lastContact || null,
                    overdue_flags: invoice.overdueFlags || {
                        daysOverdue: 0,
                        needsToFollowup: true
                    },
                    call_history: invoice.call_history || { // Ensure this is interactionHistory as per your demo data mapping
                        calls: [],
                        totalCalls: 0,
                        hasPaymentPlan: false,
                        latestOutcome: null
                    },
                    source: invoice.quickbooksInvoiceId ? 'quickbooks' : 
                           invoice.invoiceID ? 'xero' : 
                           invoice.myobInvoiceId ? 'myob' : 'unknown'
                }));
                setAccounts(mappedAccounts);
                setOutstandingInvoicesCount(invoiceResponse.data.summary?.totalInvoices || 0); // Update this count too

            } else {
                setAccounts([]);
                setInvoiceData(initialInvoiceDataState);
                setTotalInvoices(0);
            }
        } catch (error) {
            console.error('Error fetching invoice data:', error);
            setAccounts([]);
            setInvoiceData(initialInvoiceDataState);
            setTotalInvoices(0);
            ValidationErrorToast("Error", "Could not fetch invoice data.");
        } finally {
            setLoading(false);
        }
    };

    // MYOB Auth Modal Component
    const MyobAuthModal = () => {
        const handleSubmit = async () => {
            try {
                // Validate that credentials are provided for all selected files
                const missingCredentials = selectedCompanyFiles.some(file => 
                    file.requiresAuth && (!companyFileCredentials[file.id]?.username || 
                    !companyFileCredentials[file.id]?.password)
                );
    
                if (missingCredentials) {
                    ValidationErrorToast("Error", "Please provide credentials for all selected company files");
                    return;
                }
    
                // Submit credentials for each file
                for (const file of selectedCompanyFiles) {
                    if (file.requiresAuth) {
                        const response = await userService.aiRequest({
                            method: 'myob-authenticate-file',
                            data: {
                                accountId: user.id,
                                fileId: file.id,
                                username: companyFileCredentials[file.id].username,
                                password: companyFileCredentials[file.id].password
                            }
                        });
    
                        if (!response.data?.success) {
                            throw new Error(`Failed to authenticate ${file.name}`);
                        }
                    }
                }
    
                SuccessToast("Success", "MYOB company files authenticated successfully");
                setShowMyobAuthModal(false);
                
                // Refresh connection status
                if (userData) {
                    processConnectionResponses(null, { connected: true }, null, null);
                }
    
            } catch (error) {
                ValidationErrorToast("Error", `Authentication failed: ${error.message}`);
            }
        };
    
        return (
            <div className="modal" style={{
                display: showMyobAuthModal ? 'block' : 'none',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000
            }}>
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: '#1a1a2e',
                    padding: '24px',
                    borderRadius: '12px',
                    width: '90%',
                    maxWidth: '500px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                    <h3 style={{...styles.whiteText, marginBottom: '20px'}}>
                        MYOB Company File Authentication
                    </h3>
                    
                    {selectedCompanyFiles.map(file => (
                        <div key={file.id} style={{marginBottom: '20px'}}>
                            <h4 style={styles.whiteText}>{file.name}</h4>
                            {file.requiresAuth && (
                                <div style={{marginTop: '10px'}}>
                                    <input
                                        type="text"
                                        placeholder="Username"
                                        value={companyFileCredentials[file.id]?.username || ''}
                                        onChange={(e) => setCompanyFileCredentials(prev => ({
                                            ...prev,
                                            [file.id]: {
                                                ...prev[file.id],
                                                username: e.target.value
                                            }
                                        }))}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            marginBottom: '10px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: '6px',
                                            color: '#fff'
                                        }}
                                    />
                                    <input
                                        type="password"
                                        placeholder="Password"
                                        value={companyFileCredentials[file.id]?.password || ''}
                                        onChange={(e) => setCompanyFileCredentials(prev => ({
                                            ...prev,
                                            [file.id]: {
                                                ...prev[file.id],
                                                password: e.target.value
                                            }
                                        }))}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: '6px',
                                            color: '#fff'
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
    
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        marginTop: '24px',
                        justifyContent: 'flex-end'
                    }}>
                        <button
                            style={{...styles.button, ...styles.secondaryButton}}
                            onClick={() => setShowMyobAuthModal(false)}
                        >
                            Cancel
                        </button>
                        <button
                            style={{...styles.button, ...styles.primaryButton}}
                            onClick={handleSubmit}
                        >
                            Authenticate
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Test Call Modal Component
    const TestCallModal = () => {
        // Create a ref for the input element
        const inputRef = useRef(null);
        
        // Focus the input when the modal opens
        useEffect(() => {
            if (showTestCallModal && inputRef.current) {
                inputRef.current.focus();
            }
        }, [showTestCallModal]);

        return (
            <div className="modal" style={{
                display: showTestCallModal ? 'block' : 'none',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000
            }}>
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: '#1a1a2e',
                    padding: '24px',
                    borderRadius: '12px',
                    width: '90%',
                    maxWidth: '500px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                    <h3 style={{...styles.whiteText, marginBottom: '20px'}}>Test Call</h3>
                    <p style={styles.mutedText}>
                        This will initiate a test call for {selectedAccount?.customerName || ''}, 
                        but route it to your specified phone number instead of the customer.
                    </p>
                    <input
                        ref={inputRef}
                        type="tel"
                        value={testCallPhone}
                        onChange={handlePhoneChange}
                        placeholder="Enter your phone number"
                        style={{
                            width: '100%',
                            padding: '10px',
                            marginTop: '20px',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '6px',
                            color: '#fff'
                        }}
                    />
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        marginTop: '24px',
                        justifyContent: 'flex-end'
                    }}>
                        <button
                            style={{...styles.button, ...styles.secondaryButton}}
                            onClick={() => {
                                setShowTestCallModal(false);
                                setTestCallPhone('');
                                setSelectedAccount(null);
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            style={{...styles.button, ...styles.primaryButton}}
                            onClick={handleTestCall}
                        >
                            Start Test Call
                        </button>
                    </div>
                </div>
            </div>
        );
    };


    // Main component render
    return (
        <section className="main-dashboard-wrap" style={styles.container}>
            <Helmet>
                <title>MPLOI: Accounts Receivable</title>
            </Helmet>
            <div id="wrapper" style={{ height: "100vh" }}>
                {/* Sidebar */}
                <ul className={`navbar-nav bg-gradient-primary sidebar sidebar-dark accordion`} id="accordionSidebar">
                    <div>
                        <Link style={{ pointerEvents: "none" }} className="sidebar-brand d-flex align-items-center justify-content-center" to="#">
                            <img src={UtilityFunctions.getImageURL() + "db-logo.svg"} alt="Logo" />
                        </Link>
                        {!user?.user?.isAccountsSignup && <li className="nav-item">
                            <Link className="nav-link" to="/ar/affiliate/dashboard">
                                <img src={UtilityFunctions.getImageURL() + "home-icon-blank-white.svg"} alt="" />
                                <img src={UtilityFunctions.getImageURL() + "home-icon-fill-green.svg"} alt="" />
                            </Link>
                        </li>}
                    </div>

                    <div>
                        <li className='d-grid justify-content-center gap-2 text-white'>
                            <span style={{ cursor: "pointer" }}
                                onClick={handleLogout}
                            >
                                Logout
                            </span>
                        </li>
                    </div>
                </ul>
    
                {/* Content Wrapper */}
                <div id="content-wrapper" className="d-flex flex-column">
                    <div id="content">
                        {/* Top Bar */}
                        <div style={styles.topBar}>
                            <h1 style={styles.topBarTitle}>
                                {isDemoMode && demoOrgDisplayInfo ? 
                                    `Accounts Receivable - ${demoOrgDisplayInfo.name} (Demo)` : 
                                    'Accounts Receivable'}
                            </h1>
                            <div style={styles.buttonGroup}>
                                {globalOrganizationId && (
                                    <button 
                                        style={{...styles.button, ...styles.secondaryButton}}
                                        onClick={() => props.history.push(`/ar/settings/${globalOrganizationId}`)}
                                    >
                                        Settings
                                    </button>
                                )}
                            </div>
                        </div>
    
                        {/* Main Content */}
                        <div className="container-fluid">
                            {initialLoading ? (
                                <div style={{...styles.emptyState, color: '#fff'}}>
                                    <div className="spinner-border text-light" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                    <p style={{marginTop: '20px'}}>Loading your dashboard...</p>
                                </div>
                            ) : !xeroConnected && !quickbooksConnected && !myobConnected && !demoConnected && !zohoConnected && !zohoBooksConnected && !dynamicsConnected ? (
                                <EmptyState
                                    loading={loading}
                                    handleXeroConnection={handleXeroConnection}
                                    quickbooksLoading={quickbooksLoading}
                                    handleQuickbooksConnection={handleQuickbooksConnection}
                                    quickbooksConnected={quickbooksConnected}
                                    myobLoading={myobLoading}
                                    handleMyobConnection={handleMyobConnection}
                                    handleZohoConnection={handleZohoConnection}
                                    handleZohoBooksConnection={handleZohoBooksConnection}
                                    handleDynamicsConnection={handleDynamicsConnection}
                                />
                            ) : (
                                <Dashboard
                                    initialLoading={initialLoading}
                                    xeroConnected={xeroConnected}
                                    quickbooksConnected={quickbooksConnected}
                                    myobConnected={myobConnected}
                                    demoConnected={demoConnected}
                                    zohoConnected={zohoConnected}
                                    zohoBooksConnected={zohoBooksConnected}
                                    dynamicsConnected={dynamicsConnected}
                                    collectionsEnabled={collectionsEnabled}
                                    needsXeroReconnect={needsXeroReconnect}
                                    globalOrganizationId={globalOrganizationId}
                                    stats={stats}
                                    loading={loading}
                                    accounts={accounts}
                                    totalInvoices={totalInvoices}
                                    invoicesPerPage={invoicesPerPage}
                                    currentPage={currentPage}
                                    searchTerm={searchTerm}
                                    handleSearchChange={handleSearchChange}
                                    handleSearchSubmit={handleSearchSubmit}
                                    handleClearSearch={handleClearSearch}
                                    handlePageChange={handlePageChange}
                                    globalInteractions={globalInteractions}
                                    initiateCall={initiateCall}
                                    setSelectedAccount={setSelectedAccount}
                                    setTestCallPhone={setTestCallPhone}
                                    setShowTestCallModal={setShowTestCallModal}
                                    userData={userData}
                                    handleXeroConnection={handleXeroConnection}
                                    history={props.history}
                                    activeFilter={activeFilter}
                                    handleFilterChange={handleFilterChange}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Modals */}
            <TestCallModal />
            <MyobAuthModal />
            
            {/* Subscription Modal - Uncomment if needed */}
            {!isDemoMode && (
                <SubscriptionModalAccounts
                    subsModal={subsModalAccounts}
                    setSubsModal={setSubsModalAccounts}
                    history={props.history}
                />
            )}
        </section>
    );
};

export default AccountsReceivable;
